/**
 * Política de Privacidade do app Meiuca (consumidor final).
 *
 *  RASCUNHO PRELIMINAR — REVISÃO JURÍDICA OBRIGATÓRIA antes da publicação oficial.
 *
 * Endpoint público esperado: https://meiucaapp.com.br/privacidade
 *
 * Atende ao requisito da Apple Guideline 5.1.1(i) e à Google Play User Data policy:
 * a URL precisa estar pública, acessível sem login e linkada nas fichas das lojas.
 *
 * Inclui menção explícita ao ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
 * como operador dos serviços de pagamento, conforme exigido pela Resolução Conjunta
 * nº 16/2025 do BACEN para o modelo BaaS.
 *
 * Migrado de `meiuca-site` (Next.js) para `meiuca-app-web-react` (React Router).
 */

import { Body, H2, H3 } from '@meiuca/ui-components'
import { LegalPageLayout } from '../components/LegalPageLayout'

export function PrivacidadePage() {
  return (
    <LegalPageLayout
      title="Política de Privacidade"
      documentTitle="Política de Privacidade — Meiuca"
      lastUpdated="20 de maio de 2026"
    >
      <Body>
        Esta Política de Privacidade explica como o aplicativo Meiuca trata seus dados
        pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 —
        LGPD). Ao usar o app, você concorda com as práticas descritas aqui.
      </Body>

      <section>
        <H2 className="text-xl md:text-2xl mt-8 mb-3">1. Quem somos</H2>
        <Body>
          O Meiuca é operado por <strong>Rafael Balmant Letro — ME</strong>, inscrita
          no CNPJ sob nº <strong>27.422.545/0001-42</strong> e Inscrição Municipal
          nº <strong>00098036</strong>, com sede na{' '}
          <strong>Rua São Marcos, 370 — Bairro São Vito — Americana/SP — CEP 13472-200</strong>.
          Para fins da LGPD, somos o <strong>controlador</strong> dos dados pessoais
          coletados pelo aplicativo. Para exercer qualquer direito ou tirar dúvidas sobre
          privacidade, escreva para{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">2. Quais dados coletamos</H2>
        <H3 className="text-lg mt-4 mb-2">Dados que você fornece diretamente</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li><strong>Telefone celular</strong> — para criar sua conta e autenticar via SMS (OTP).</li>
          <li><strong>Nome completo</strong> e <strong>nickname</strong> — exibidos no seu perfil.</li>
          <li><strong>Senha</strong> — armazenada de forma criptografada (hash); nunca temos acesso ao texto puro.</li>
          <li>
            <strong>CPF</strong> — solicitado <em>apenas</em> quando você opta por pagar
            uma reserva via Pix, para que a cobrança possa ser emitida pelo ASAAS.
          </li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">Dados gerados pelo uso</H3>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Histórico de reservas, planos e treinos contratados.</li>
          <li>Mensagens trocadas com o suporte ou parceiros (quando aplicável).</li>
          <li>Logs técnicos básicos (data/hora de acesso, versão do app, modelo do dispositivo).</li>
        </ul>

        <H3 className="text-lg mt-6 mb-2">Localização</H3>
        <Body>
          Hoje o Meiuca <strong>não acessa o GPS</strong> do seu dispositivo. Buscas por
          endereço usam APIs de pesquisa textual (MapKit no iOS, Google Places no Android).
          Caso adicionemos uma funcionalidade que dependa da sua localização atual no futuro,
          pediremos sua permissão explícita pelo sistema operacional.
        </Body>

        <H3 className="text-lg mt-6 mb-2">Dados financeiros</H3>
        <Body>
          Pagamentos são processados pelo{' '}
          <strong>
            ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
          </strong>
          {' '}(CNPJ 19.540.550/0001-21), instituição de pagamento autorizada pelo Banco
          Central do Brasil. O Meiuca <strong>não armazena</strong> dados bancários, dados
          de cartão, chaves Pix nem credenciais de transação. Recebemos do ASAAS apenas
          a confirmação de pagamento (status, valor, momento da liquidação e identificador
          da transação), o que é necessário para atualizar o status da sua reserva.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">3. Para que usamos esses dados</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>Autenticar sua conta e manter você logado entre sessões.</li>
          <li>Processar reservas, planos e mensalidades contratados.</li>
          <li>Comunicar confirmações, lembretes e mudanças relacionadas ao serviço.</li>
          <li>Atender obrigações legais, fiscais e contratuais.</li>
          <li>Investigar fraudes e proteger a segurança da plataforma.</li>
        </ul>
        <Body className="mt-4">
          Não usamos seus dados para perfilamento publicitário nem para vendas a terceiros.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">4. Com quem compartilhamos</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            <strong>Arenas, centros de treinamento e profissionais parceiros</strong> que
            recebem sua reserva — apenas o necessário (nome, contato, horário) para que
            possam te receber.
          </li>
          <li>
            <strong>Provedor de envio de SMS</strong> — para entregar o código OTP.
          </li>
          <li>
            <strong>
              ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
            </strong>
            {' '}— quando você opta por pagar uma reserva via Pix, compartilhamos seu nome,
            telefone e CPF para que o ASAAS possa emitir a cobrança e processar o pagamento,
            cumprindo as exigências regulatórias do Banco Central (prevenção a fraude,
            PLD/FT e KYC do pagador).
          </li>
          <li>
            <strong>Autoridades públicas</strong>, quando exigido por lei ou ordem judicial.
          </li>
        </ul>
        <Body className="mt-4">
          O tratamento de dados realizado pelo ASAAS é regido pela{' '}
          <a
            href="https://www.asaas.com/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            Política de Privacidade do ASAAS
          </a>
          .
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">5. Por quanto tempo guardamos</H2>
        <Body>
          Dados de cadastro são mantidos enquanto sua conta estiver ativa. Quando você
          solicita a exclusão (veja a página{' '}
          <a href="/excluir-conta" className="text-brand-primary hover:underline">
            Excluir conta
          </a>
          ), removemos ou anonimizamos esses dados em até 30 dias úteis. Registros de
          pagamentos e reservas concluídas são mantidos por <strong>5 anos</strong> em
          forma desvinculada da sua identidade quando possível, para cumprir obrigações
          fiscais, contratuais e regulatórias (Resolução Conjunta nº 16/2025 do BACEN).
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">6. Seus direitos</H2>
        <Body>A LGPD (art. 18) garante a você:</Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-2">
          <li>Confirmar a existência e acessar seus dados.</li>
          <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
          <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários.</li>
          <li>Pedir a portabilidade para outro fornecedor.</li>
          <li>Revogar consentimentos quando aplicável.</li>
          <li>Apresentar reclamação à ANPD.</li>
        </ul>
        <Body className="mt-4">
          Para exercer qualquer um desses direitos, escreva para{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">7. Segurança</H2>
        <Body>
          Toda comunicação entre o app e nossos servidores é feita por HTTPS. Senhas
          são armazenadas com hash. Tokens de autenticação são guardados em
          armazenamento seguro do sistema operacional (Keychain no iOS, EncryptedSharedPreferences
          no Android). Nenhum sistema é totalmente imune a incidentes — em caso de
          incidente que afete seus dados, comunicaremos você e a ANPD nos prazos legais.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">8. Crianças e adolescentes</H2>
        <Body>
          O Meiuca não é direcionado a menores de 13 anos. Se você tem entre 13 e 18 anos,
          é necessário consentimento de um responsável legal. Se identificarmos coleta
          inadvertida de dados de menores sem o consentimento adequado, removeremos esses
          dados imediatamente.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">9. Alterações nesta política</H2>
        <Body>
          Podemos atualizar esta política para refletir mudanças no app ou em obrigações
          legais. Sempre que houver alteração relevante, atualizaremos a data no topo
          e — quando a mudança for significativa — comunicaremos você no app.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">10. Contato</H2>
        <Body>
          Encarregado pelo tratamento de dados (DPO):{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>.
        </Body>
      </section>
    </LegalPageLayout>
  )
}
